/**
 * Time-bucketing helper for member-avatar activity dots.
 *
 * Translates a `lastActive` ISO timestamp into a `{ label, time, color }`
 * triple that drives the avatar tooltip:
 *
 *   - active now (<60 min)  → green
 *   - last active Nh        → amber
 *   - last active Nd / Nw   → grey
 *
 * The `t` argument is passed in (rather than imported from
 * `react-i18next`) so the helper can run in shared without pulling
 * `react-i18next` into the shared dependency surface — `member-avatar`
 * on web and the eventual RN equivalent both call `useTranslation()`
 * locally and hand it down.
 */
export type ActivityStatus = {
  label: string;
  time: string;
  color: string;
} | null;

export type ActivityTranslator = (
  key: string,
  options?: Record<string, unknown>,
) => string;

export function getActivityStatus(
  lastActive: string | null | undefined,
  t: ActivityTranslator,
): ActivityStatus {
  if (!lastActive) return null;
  const diffMs = Date.now() - new Date(lastActive).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 60)
    return {
      label: t('studyGroups.activity.activeNow'),
      time: '',
      color: 'bg-green-500',
    };
  if (diffHours < 24)
    return {
      label: t('studyGroups.activity.lastActive', { time: `${diffHours}h` }),
      time: `${diffHours}h`,
      color: 'bg-amber-400',
    };
  if (diffDays < 7)
    return {
      label: t('studyGroups.activity.lastActive', { time: `${diffDays}d` }),
      time: `${diffDays}d`,
      color: 'bg-secondary-300',
    };
  const diffWeeks = Math.floor(diffDays / 7);
  return {
    label: t('studyGroups.activity.lastActive', { time: `${diffWeeks}w` }),
    time: `${diffWeeks}w`,
    color: 'bg-secondary-300',
  };
}

/**
 * Which timestamp an activity dot should bucket for one PERSON on a group
 * surface (sidebar deck rows, tile facepiles, member lists).
 *
 * A study group only knows when someone last opened THAT group, so a mate who
 * is in Flashka right now — studying anywhere else — reads as "last active 2h"
 * until they happen to open the group. Whoever has a public profile therefore
 * wins with their GLOBAL presence (`social_profiles.last_active_at`, the same
 * field their profile and every follow list show); everyone off the social
 * layer keeps the group timestamp, which is all that exists for them.
 *
 * The newer of the two is the answer, not the global one outright: a group
 * surface must never report someone as staler than this group has just seen
 * them (a presence stamp is throttled, a group open is not).
 */
export function resolveMemberPresence(
  globalLastActive: string | null | undefined,
  groupLastActive: string | null | undefined,
): string | null {
  if (!globalLastActive) return groupLastActive ?? null;
  if (!groupLastActive) return globalLastActive;
  // Parsed, not compared as strings: the two sides come from different stores
  // (Postgres presence vs the DO's own ISO stamps) and need not be spelled the
  // same way. An unparseable value loses rather than poisoning the dot.
  const global = Date.parse(globalLastActive);
  const group = Date.parse(groupLastActive);
  if (Number.isNaN(global)) return groupLastActive;
  if (Number.isNaN(group)) return globalLastActive;
  return global > group ? globalLastActive : groupLastActive;
}
